/**
 * 复制到剪贴板 —— `copy` → 成功后切 `check`，1.5s 回弹（06-ICON-MAP.md §6）。
 *
 * 失败路径不能沉默：`navigator.clipboard` 在非安全上下文（http 访问内网 IP）下不存在，
 * 这正是自托管场景的常态。此时降级为选中提示，而不是让按钮点了没反应。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';

type CopyState = 'idle' | 'copied' | 'unavailable';

const REVERT_MS = 1_500;

export interface CopyButtonProps {
  readonly value: string;
  /** 用于 aria-label，如「复制 license_id」 */
  readonly what: string;
}

export function CopyButton({ value, what }: CopyButtonProps) {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = useCallback(async () => {
    const clipboard = globalThis.navigator?.clipboard;
    if (clipboard === undefined) {
      setState('unavailable');
      return;
    }
    try {
      await clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('unavailable');
    }
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), REVERT_MS);
  }, [value]);

  const label =
    state === 'copied'
      ? `已复制${what}`
      : state === 'unavailable'
        ? '当前环境不支持剪贴板（需 HTTPS 或 localhost），请手动选中复制'
        : `复制${what}`;

  return (
    <button
      type="button"
      className="btn btn--quiet btn--icon"
      onClick={() => {
        void handleCopy();
      }}
      aria-label={label}
      title={label}
    >
      <Icon
        name={state === 'copied' ? 'check' : state === 'unavailable' ? 'circle-alert' : 'copy'}
        size={ICON_SIZE.inline}
      />
    </button>
  );
}
